import React, { useEffect, useState } from "react";
import { Stack } from "@origin-digital/ods-core";
import { CloseTransferStatusEnum } from "../../types";
import {
  ConnectionTypeEnum,
  ServiceTypeEnum,
} from "src/components/Premises/types";
import { handoverTypeToLabelMap, serviceTypeToName } from "../helper";
import { apiClient } from "src/api/apiService";
import { useNavigate } from "react-router-dom";
import { CloseTransferData } from "../types";
import { normaliseDate } from "../../../../api/apiHelper";
import { useCloseTransferFormProps } from "./useCloseTransferFormProps";
import { CloseTransferFormAutomatedActionSection } from "./CloseTransferFormAutomatedActionSection";
import { CloseTransferFormSubmittedServiceSection } from "./CloseTransferFormSubmittedServiceSection";
import { useCloseTransferFormSetFieldValueEffect } from "./useCloseTransferFormSetFieldValueEffect";
import { CloseTransferFormAutomatedHeaderSection } from "./CloseTransferFormAutomatedHeaderSection";
import { CloseTransferFormAutomatedServiceSection } from "./CloseTransferFormAutomatedServiceSection";
import CloseTransferImportantInfo from "./CloseTransferImportantInfo";
import { CloseTransferAccountSection } from "./CloseTransferAccountSection";

const mapActionTypeToStatus = {
  cancel: CloseTransferStatusEnum.CANCELLED,
  action: CloseTransferStatusEnum.PROCESSED,
};

export const CloseTransferFormAutomated: React.FC = () => {
  const navigate = useNavigate();
  const {
    formikContext,
    selectedFormState,
    newPremiseFormContext,
    formBehavior,
  } = useCloseTransferFormProps();
  const { values } = formikContext;
  const { closeTransfer } = values;
  const { setDisplayingSnackbar, setFormState } = newPremiseFormContext;
  const { electricityService, gasServices, closeTransferPayload } =
    selectedFormState;
  useCloseTransferFormSetFieldValueEffect({
    formikContext,
    formBehavior,
  });
  const {
    hasElectricitySection,
    isElectricityEnable,
    hasGasSection,
    isGasEnable,
  } = formBehavior;

  const [elecDates, setElecDates] = useState<{ dates: string[] }>();
  const [gasDates, setGasDates] = useState<{ dates: string[] }>();
  const getMoveOutDates = async (service: ServiceTypeEnum, nmiMirn: string) => {
    try {
      const response = await apiClient.get<{
        dates: string[];
      }>(`/close-transfer/move-out-dates/${nmiMirn}/${service}`);
      if (service === ServiceTypeEnum.ELECTRICITY) setElecDates(response.data);
      else setGasDates(response.data);
    } catch (err) {
      console.error("Error fetching dates for elec");
      setDisplayingSnackbar({
        message: "Error fetching dates for elec.",
        tone: "critical",
      });
    }
  };
  useEffect(() => {
    const mirn = values.gasMeter?.mirn || values.both?.allocateNmiOrderNumber;
    if (hasGasSection && isGasEnable && mirn)
      getMoveOutDates(ServiceTypeEnum.GAS, mirn);
  }, [
    isGasEnable,
    hasGasSection,
    values.gasMeter?.mirn,
    values.both?.allocateNmiOrderNumber,
  ]);

  useEffect(() => {
    if (hasElectricitySection && isElectricityEnable && !!values.nmi)
      getMoveOutDates(ServiceTypeEnum.ELECTRICITY, values.nmi);
  }, [hasElectricitySection, isElectricityEnable, values.nmi]);

  const submitCloseTransfer = async () => {
    const serviceTypeLabel = handoverTypeToLabelMap[closeTransfer.handoverType];
    const _closeTransferPayload = closeTransfer.serviceTypes
      .map((serviceType) => ({
        service_type: serviceType,
        move_out_date:
          serviceType === ServiceTypeEnum.ELECTRICITY
            ? normaliseDate(closeTransfer.elecPreferDate, null)
            : normaliseDate(closeTransfer.gasPreferDate, null),
        chosen_action: closeTransfer.handoverType,
        close_transfer_status: CloseTransferStatusEnum.SUBMITTED,
        journey_id:
          serviceType === ServiceTypeEnum.ELECTRICITY
            ? electricityService?.journeyId
            : gasServices?.gasMeter?.journeyId || gasServices?.both?.journeyId,
      }))
      .filter((s) => !closeTransferPayload?.[s.service_type]?.id);

    // postBody start
    try {
      const ctPayload = {
        account_holder: {
          customer_first_name: closeTransfer.accountHolder?.firstName,
          customer_last_name: closeTransfer.accountHolder?.lastName,
          customer_phone_number: closeTransfer.accountHolder?.phone,
          customer_email: closeTransfer.accountHolder?.email,
        },
        close_transfers: [..._closeTransferPayload],
      };
      console.log("closetransferPayload", ctPayload);
      //postBody end
      const response = await apiClient.post(
        `/close-transfer/close-transfer-journey-call`,
        ctPayload
      );
      if (response.status) {
        navigate("/", {
          state: {
            submitted: true,
            journeyId:
              electricityService?.journeyId ||
              gasServices?.gasMeter?.journeyId ||
              gasServices?.both?.journeyId,
            snackbarMessage: "Close/transfer request lodged",
          },
        });
      }
    } catch (error) {
      setDisplayingSnackbar({
        message: `Failed to ${serviceTypeLabel} account(s). Try again and if issue persists, contact us.`,
        tone: "critical",
      });
    }
  };

  const onUpdate = async (
    serviceType: ServiceTypeEnum,
    actionType: "cancel" | "action"
  ) => {
    try {
      const updatedCloseTransferPayload = closeTransferPayload![serviceType];

      const { data } = await apiClient.put<CloseTransferData[]>(
        `/close-transfer`,
        [
          {
            ...updatedCloseTransferPayload,
            close_transfer_status: mapActionTypeToStatus[actionType],
          },
        ]
      );

      if (actionType === "action") {
        setDisplayingSnackbar({
          message: `${serviceTypeToName[serviceType]} account closed`,
          tone: "positive",
        });
      }

      if (actionType === "cancel") {
        setDisplayingSnackbar({
          message: `${serviceTypeToName[serviceType]} account closure cancelled`,
          tone: "positive",
        });
      }

      const updatedCloseTransferData = data.find(
        (service) => service.service_type === serviceType
      );

      if (serviceType === ServiceTypeEnum.ELECTRICITY) {
        setFormState((prev) => ({
          ...prev,
          closeTransfer: {
            ...prev.closeTransfer,
            [serviceType]: updatedCloseTransferData,
          },
          electricityService: {
            ...prev.electricityService,
            requestStatus: updatedCloseTransferData?.request_status,
          },
        }));
      }

      if (serviceType === ServiceTypeEnum.GAS) {
        const gasServiceName: "gasMeter" | "both" =
          updatedCloseTransferData?.connection_type ===
          ConnectionTypeEnum.METER_FIX
            ? "gasMeter"
            : "both";
        setFormState((prev) => ({
          ...prev,
          gasServices: {
            ...prev.gasServices,
            [gasServiceName]: {
              ...prev.gasServices![gasServiceName],
              requestStatus: updatedCloseTransferData?.request_status,
            },
          },
          closeTransfer: {
            ...prev.closeTransfer,
            [serviceType]: updatedCloseTransferData,
          },
        }));
      }
    } catch (error) {
      if (actionType === "action") {
        setDisplayingSnackbar({
          message: "Failed to close account, please try again",
          tone: "caution",
        });
      }

      if (actionType === "cancel") {
        setDisplayingSnackbar({
          message: "Failed to cancel account closure, please try again",
          tone: "caution",
        });
      }
    }
  };

  return (
    <Stack
      space={{
        xs: "xxlarge",
        md: "xxxlarge",
      }}
    >
      {/* Form container */}
      <Stack
        space={{
          xs: "xlarge",
          md: "xxlarge",
        }}
      >
        {/* Header */}
        <CloseTransferFormAutomatedHeaderSection />
        {/* End Header */}

        {/* Services */}
        <CloseTransferFormAutomatedServiceSection
          onUpdate={onUpdate}
          elecDates={elecDates}
          gasDates={gasDates}
        />
        {/* End Services */}

        <CloseTransferAccountSection />
        {(isElectricityEnable || isGasEnable) && <CloseTransferImportantInfo />}
      </Stack>
      {/* End Form container */}

      {/* Actions */}
      <CloseTransferFormAutomatedActionSection
        submitCloseTransfer={submitCloseTransfer}
      />
      {/* End Actions */}

      {/* Submitted services */}
      <CloseTransferFormSubmittedServiceSection />
      {/* End Submitted services */}
    </Stack>
  );
};
